/**
 * Dialogo "Dati e relazioni" per Company360.
 *
 * Costruisce le sezioni edit specifiche di Company360 (Informazioni / Sede / Company Year, in base
 * al tipo di entità collegata — Company/FoodService/Service/Retailer) e le passa alla shell
 * generica `Relations360Dialog`, che gestisce le sezioni relazione e lo staging.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ENTITY } from '../utils/entities';
import { client } from '../utils/client';
import {
	getConnectedRef,
	getEditSectionKeys,
	EDIT_SECTION_LABELS,
	editSummaryText,
	buildConnectedSummaryQuery,
	companyYearText,
	LINKED_COMPANY_YEARS_QUERY,
	ConnectedRef,
} from '../utils/entities/companyData360';
import { CompanyData360EditView } from './CompanyData360EditView';
import { EditSectionSpec, Relations360Dialog } from './relations360Shared';

interface Relations360ModalProps {
	entity: any;
	entityName: ENTITY;
	isOpen: boolean;
	onClose: () => void;
}

const FAIR_CERT_FIELDS = [
	'Relation360_FairParticipations',
	'Relation360_CompanyCertifications',
];

interface CompanyEditData {
	connectedRef: ConnectedRef | null;
	summary: any;
	linkedCompanyYear: any;
}

export const Relations360Modal: React.FC<Relations360ModalProps> = ({
	entity,
	entityName,
	isOpen,
	onClose,
}) => {
	const [editData, setEditData] = useState<CompanyEditData>({
		connectedRef: null,
		summary: null,
		linkedCompanyYear: null,
	});

	const loadEditData = useCallback(async () => {
		const connectedRef = getConnectedRef(entity);
		if (!connectedRef || !connectedRef.config) {
			setEditData({ connectedRef: null, summary: null, linkedCompanyYear: null });
			return;
		}
		let summary: any = null;
		let linkedCompanyYear: any = null;
		try {
			const r = await client.query<any, any>({
				query: buildConnectedSummaryQuery(connectedRef.config),
				variables: { id: connectedRef.id },
				fetchPolicy: 'no-cache',
			});
			summary = (r.data && r.data.entity) || null;
		} catch (e) {
			summary = null;
		}
		try {
			const r = await client.query<any, any>({
				query: LINKED_COMPANY_YEARS_QUERY,
				variables: { id: entity.id },
				fetchPolicy: 'no-cache',
			});
			linkedCompanyYear =
				((r.data && r.data.entity && r.data.entity.Relation360_CompanyYears) ||
					[])[0] || null;
		} catch (e) {
			linkedCompanyYear = null;
		}
		setEditData({ connectedRef, summary, linkedCompanyYear });
	}, [entity]);

	useEffect(() => {
		if (isOpen) loadEditData();
	}, [isOpen, loadEditData]);

	const cr = editData.connectedRef;

	const editSections: EditSectionSpec[] = useMemo(() => {
		if (!cr || !cr.config) return [];
		return getEditSectionKeys(cr.config).map((key) => ({
			key,
			labelKey: EDIT_SECTION_LABELS[key],
			summaryText:
				key === 'companyYear'
					? companyYearText(editData.linkedCompanyYear)
					: editSummaryText(cr.config as any, key, editData.summary),
			render: (onSaved: () => void) => (
				<CompanyData360EditView
					config={cr.config as any}
					sectionKey={key}
					connectedId={cr.id}
					company360Id={entity.id}
					summary={editData.summary}
					linkedCompanyYear={editData.linkedCompanyYear}
					onSaved={onSaved}
				/>
			),
		}));
	}, [cr, editData, entity]);

	// Fiere/Certificazioni sono solo per Company360 che punta ad una Company.
	const hiddenRelationFields =
		cr && cr.config && cr.config.hasFairAndCertifications ? [] : FAIR_CERT_FIELDS;

	return (
		<Relations360Dialog
			entity={entity}
			entityName={entityName}
			isOpen={isOpen}
			onClose={onClose}
			editSections={editSections}
			hiddenRelationFields={hiddenRelationFields}
			onReloadEdit={loadEditData}
		/>
	);
};